import { onMounted, ref, type Ref } from 'vue';

import { getOrderByYear } from '../../../utils';

interface UseReturnOrdersOptions {
  /** Same lookback window the orders widget uses. */
  showPreviousOrderDays?: number;
  statusForOrdersNotShown?: string[];
  userInfo?: unknown;
  enabled?: boolean;
}

interface UseReturnOrdersApi {
  orders: Ref<any[]>;
  isLoading: Ref<boolean>;
  hasError: Ref<boolean>;
}

/**
* Fetches the orders the return cards are built from.
*
* This deliberately does not reuse the list the My Orders widget reports back.
* That list is capped by `maxNoOfOrders` — three today — and the cap is spent
* on the most recent orders regardless of whether they have a return. A
* customer with three recent orders and an older active return would see no
* card at all.
*
* Passing 0 as the cap disables it in `getOrderByYear`, so we get every order
* in the window and let the return filter decide what renders. The date window
* and the excluded statuses stay the same, so the two widgets still agree on
* which orders exist.
*/
const useReturnOrders = (options: UseReturnOrdersOptions = {}): UseReturnOrdersApi => {
  const orders = ref<any[]>([]);
  const isLoading = ref(false);
  const hasError = ref(false);

  const NO_CAP = 0;

  onMounted(async () => {
    if (options.enabled === false) return;

    isLoading.value = true;
    hasError.value = false;

    try {
      orders.value = await getOrderByYear(
        options.showPreviousOrderDays,
        options.statusForOrdersNotShown,
        options.userInfo,
        NO_CAP,
      ) ?? [];
    } catch {
      hasError.value = true;
      orders.value = [];
    } finally {
      isLoading.value = false;
    }
  });

  return { orders, isLoading, hasError };
};

export default useReturnOrders;
